import { DrumParentCategories, MelodicParentCategories } from '@arpeggi/shared'
import { ComponentProps, FC, useMemo } from 'react'
import { ChevronDown } from '../../../core/icons'
import * as Dropdown from './Dropdown'

interface InstrumentProps {
  onSelect?: (tags: string[]) => void
  selected: string[]
}

type InstrumentDropdownProps = Omit<ComponentProps<typeof Dropdown.Trigger>, 'onSelect'> &
  InstrumentProps

interface Instrument {
  label: string
  values: string[]
}
export interface InstrumentGroup {
  label: string
  instruments: Instrument[]
}

const segmentTrackFilter = (filter) => {
  window.analytics.track('Button Clicked', {
    button: 'instrument',
    filter: filter
  })
}

export const getInstrumentGroups = (): InstrumentGroup[] => {
  const melodics: InstrumentGroup[] = Object.keys(MelodicParentCategories).map(
    (groupName): InstrumentGroup => ({
      label: groupName,
      instruments: MelodicParentCategories[groupName].map(
        (i): Instrument => ({ label: i, values: [i] })
      )
    })
  )
  const drums: InstrumentGroup = {
    label: 'Drums',
    instruments: Object.keys(DrumParentCategories.OneShot).map(
      (instrumentName): Instrument => ({
        label: instrumentName,
        values:
          DrumParentCategories.OneShot[instrumentName].length > 0
            ? DrumParentCategories.OneShot[instrumentName]
            : [instrumentName]
      })
    )
  }
  return [drums, ...melodics]
}

const hasInstrument = (selected: Set<string>, values: string[]) => {
  for (let i = 0; i < values.length; i++) {
    if (!selected.has(values[i])) return false
  }
  return true
}

const InstrumentDropdown: FC<InstrumentDropdownProps> = ({ selected, onSelect, ...props }) => {
  const instrumentGroups = useMemo(() => getInstrumentGroups(), [])
  const _selected = useMemo(() => new Set(selected), [selected.length])
  const handleSelect = (instruments: string[], instrument: string) => (evt) => {
    evt.preventDefault()
    onSelect(instruments)
    segmentTrackFilter(instrument)
  }
  return (
    <Dropdown.Root dir="ltr">
      <Dropdown.Trigger active={selected.length > 0} {...props}>
        Instrument
        <ChevronDown />
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          mobile={{ '@initial': true, '@tablet': false }}
          css={{ '@tablet': { width: '626px', maxHeight: '600px' } }}
          side={'bottom'}
          align={'start'}
          sideOffset={4}
        >
          {instrumentGroups.map(({ label, instruments }, i) => (
            <Dropdown.Group key={label} css={{ '@tablet': { width: '150px' } }}>
              <Dropdown.Label>{label}</Dropdown.Label>
              {instruments.map((instrument, j) => (
                <Dropdown.CheckboxItem
                  key={instrument.label}
                  checked={hasInstrument(_selected, instrument.values)}
                  onSelect={handleSelect(instrument.values, instrument.label)}
                >
                  {instrument.label}
                  <Dropdown.Checkbox />
                </Dropdown.CheckboxItem>
              ))}
            </Dropdown.Group>
          ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  )
}

export default InstrumentDropdown
